import { COLORS, DOMAIN_COLORS, BG_BASE, BG_SURFACE } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";

const STRENGTH_TO_DOMAIN = {
  Analytical: "Strategic Thinking", Context: "Strategic Thinking", Futuristic: "Strategic Thinking", Ideation: "Strategic Thinking", Input: "Strategic Thinking", Intellection: "Strategic Thinking", Learner: "Strategic Thinking", Strategic: "Strategic Thinking",
  Achiever: "Executing", Arranger: "Executing", Belief: "Executing", Consistency: "Executing", Deliberative: "Executing", Discipline: "Executing", Focus: "Executing", Responsibility: "Executing", Restorative: "Executing",
  Activator: "Influencing", Command: "Influencing", Communication: "Influencing", Competition: "Influencing", Maximizer: "Influencing", "Self-Assurance": "Influencing", Significance: "Influencing", Woo: "Influencing",
  Adaptability: "Relationship Building", Connectedness: "Relationship Building", Developer: "Relationship Building", Empathy: "Relationship Building", Harmony: "Relationship Building", Includer: "Relationship Building", Individualization: "Relationship Building", Positivity: "Relationship Building", Relator: "Relationship Building",
};

const sampleData = {
  score: 7.4,
  engagementType: "Cloud Migration",
  industry: "Financial Services",
  members: [
    { name: "Todd Korando",   strengths: ["Strategic", "Analytical", "Learner", "Focus", "Achiever"] },
    { name: "Shirley Cortez", strengths: ["Woo", "Communication", "Maximizer", "Self-Assurance", "Activator"] },
    { name: "Michael Monske", strengths: ["Harmony", "Consistency", "Responsibility", "Developer", "Analytical"] },
  ],
  domains: [
    { name: "Strategic Thinking",    coverage: 2, total: 3, relevance: "primary",   score: 8, strengths: ["Strategic", "Analytical", "Learner"] },
    { name: "Executing",             coverage: 2, total: 3, relevance: "primary",   score: 8, strengths: ["Focus", "Achiever", "Consistency", "Responsibility"] },
    { name: "Influencing",           coverage: 1, total: 3, relevance: "secondary", score: 5, strengths: ["Woo", "Communication", "Maximizer"] },
    { name: "Relationship Building", coverage: 1, total: 3, relevance: "excluded",  score: 5, strengths: ["Harmony", "Developer"] },
  ],
  pairings: [
    { pair: "Korando's Achiever + Monske's Responsibility", description: "Mutual accountability — both driven to follow through on commitments" },
    { pair: "Cortez's Activator + Korando's Focus",         description: "Initiative meets directed execution — starts fast and stays on track" },
    { pair: "Monske's Harmony + Cortez's Communication",    description: "Smooth stakeholder management — conflict resolution meets clear messaging" },
  ],
};

function DomainCard({ domain }) {
  const color      = DOMAIN_COLORS[domain.name] || COLORS.textMuted;
  const isExcluded = domain.relevance === "excluded";
  const isSecondary = domain.relevance === "secondary";
  const opacity    = isExcluded ? 0.4 : isSecondary ? 0.7 : 1.0;
  const coveragePct = domain.total > 0 ? (domain.coverage / domain.total) * 100 : 0;

  return (
    <div style={{ background: BG_BASE, borderRadius: 6, padding: "12px 14px", opacity, border: isExcluded ? `1px dashed ${COLORS.cardBorder}` : `1px solid ${color}30` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: isExcluded ? COLORS.textMuted : color }}>{domain.name}</span>
        <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, padding: "2px 6px", borderRadius: 8, background: domain.relevance === "primary" ? `${color}20` : "transparent", color: isExcluded ? COLORS.textMuted : domain.relevance === "primary" ? color : COLORS.textMuted }}>
          {domain.relevance === "primary" ? "PRIMARY" : domain.relevance === "secondary" ? "SECONDARY" : "NOT REQUIRED"}
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: isExcluded ? COLORS.textMuted : color, marginBottom: 4 }}>
        {isExcluded ? "—" : `${(domain.score || 0).toFixed(1)}`}
      </div>
      <div style={{ width: "100%", height: 4, background: BG_SURFACE, borderRadius: 2, marginBottom: 4 }}>
        <div style={{ width: `${coveragePct}%`, height: "100%", background: isExcluded ? COLORS.textMuted : color, borderRadius: 2 }} />
      </div>
      <div style={{ fontSize: 10, color: COLORS.textMuted }}>
        {domain.coverage}/{domain.total} members ({coveragePct.toFixed(1)}%)
      </div>
      {isExcluded && <div style={{ fontSize: 9, color: COLORS.textMuted, fontStyle: "italic", marginTop: 4 }}>Not required for this engagement type</div>}
    </div>
  );
}

export default function StrengthsFinderAlignment({ data, onEmployeeClick }) {
  const d = data || sampleData;

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>StrengthsFinder Alignment</h3>
        <span style={{ fontSize: 22, fontWeight: 700, color: COLORS.purple }}>{(d.score || 0).toFixed(1)}</span>
      </div>

      {d.engagementType && (
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 16, padding: "6px 10px", background: BG_BASE, borderRadius: 4 }}>
          <strong style={{ color: COLORS.text }}>{d.engagementType}</strong>{d.industry ? ` (${d.industry})` : ""} —
          Primary: <span style={{ color: DOMAIN_COLORS["Strategic Thinking"] }}>Strategic Thinking</span>, <span style={{ color: DOMAIN_COLORS["Executing"] }}>Executing</span> |
          Secondary: <span style={{ color: DOMAIN_COLORS["Influencing"] }}>Influencing</span>
        </div>
      )}

      {/* Member strengths */}
      {(d.members || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Team Strengths</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.members.map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: BG_BASE, borderRadius: 4 }}>
                <span style={{ minWidth: 110 }}>
                  <ClickableName name={m.name} onClick={onEmployeeClick} style={{ fontSize: 12, fontWeight: 600 }} />
                </span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {(m.strengths || []).map((s, j) => {
                    const domain = STRENGTH_TO_DOMAIN[s] || "Unknown";
                    const color  = DOMAIN_COLORS[domain] || COLORS.textMuted;
                    return (
                      <span key={j} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: `${color}20`, color, fontWeight: 500 }}>{s}</span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain grid */}
      {(d.domains || []).length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {d.domains.map((dom, i) => <DomainCard key={i} domain={dom} />)}
        </div>
      )}

      {/* Pairings */}
      {(d.pairings || []).length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Complementary Pairings</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.pairings.map((p, i) => (
              <div key={i} style={{ padding: "8px 12px", background: BG_BASE, borderRadius: 4, borderLeft: `3px solid ${COLORS.purple}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.purple }}>{p.pair}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{p.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
